import React, { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ZoomIn, ZoomOut, RotateCw, X } from 'lucide-react';

interface ProfilePhotoCropperProps {
  imageFile: File;
  onCrop: (croppedFile: File) => void;
  onCancel: () => void;
  isOpen: boolean;
}

export const ProfilePhotoCropper: React.FC<ProfilePhotoCropperProps> = ({
  imageFile,
  onCrop,
  onCancel,
  isOpen,
}) => {
  const [imageSrc, setImageSrc] = useState<string>('');
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [scale, setScale] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
  const containerRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const CROP_SIZE = 300; // Size of the circular crop area

  // Load image when file changes
  useEffect(() => {
    if (imageFile) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const src = e.target?.result as string;
        setImageSrc(src);
        
        // Load image to get dimensions
        const img = new Image();
        img.onload = () => {
          setImageSize({ width: img.width, height: img.height });
          // Calculate initial scale to show full image
          // We want the entire image to be visible initially, so scale based on the larger dimension
          const maxDimension = Math.max(img.width, img.height);
          // Scale so the larger dimension fits inside the crop circle
          // Use a scale that makes the image fit comfortably (slightly smaller than crop size)
          const initialScale = (CROP_SIZE / maxDimension) * 0.85; // 0.85 ensures full image is visible
          setScale(initialScale);
          setPosition({ x: 0, y: 0 });
        };
        img.src = src;
      };
      reader.readAsDataURL(imageFile);
    }
  }, [imageFile]);

  // Reset when dialog opens
  useEffect(() => {
    if (isOpen && imageFile) {
      setScale(1);
      setPosition({ x: 0, y: 0 });
      setRotation(0);
    }
  }, [isOpen, imageFile]);

  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    setDragStart({
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    });
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging) return;
    setPosition({
      x: e.clientX - dragStart.x,
      y: e.clientY - dragStart.y,
    });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    const touch = e.touches[0];
    setIsDragging(true);
    setDragStart({
      x: touch.clientX - position.x,
      y: touch.clientY - position.y,
    });
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging) return;
    const touch = e.touches[0];
    setPosition({
      x: touch.clientX - dragStart.x,
      y: touch.clientY - dragStart.y,
    });
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  const handleZoomIn = () => {
    setScale((prev) => {
      const newScale = prev * 1.2;
      return Math.min(Math.max(newScale, 0.5), 3); // Clamp between 0.5 and 3
    });
  };

  const handleZoomOut = () => {
    setScale((prev) => {
      const newScale = prev / 1.2;
      return Math.min(Math.max(newScale, 0.5), 3); // Clamp between 0.5 and 3
    });
  };

  const handleRotate = () => {
    setRotation((prev) => (prev + 90) % 360);
  };

  const handleCrop = () => {
    if (!imageRef.current || !canvasRef.current || !containerRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const img = imageRef.current;
    const containerRect = containerRef.current.getBoundingClientRect();
    
    canvas.width = CROP_SIZE;
    canvas.height = CROP_SIZE;

    // Clear canvas
    ctx.clearRect(0, 0, CROP_SIZE, CROP_SIZE);

    // Create circular clipping path
    ctx.beginPath();
    ctx.arc(CROP_SIZE / 2, CROP_SIZE / 2, CROP_SIZE / 2, 0, Math.PI * 2);
    ctx.clip();

    // Get image natural dimensions
    const imgNaturalWidth = img.naturalWidth;
    const imgNaturalHeight = img.naturalHeight;
    
    // Get displayed image dimensions
    const imgRect = img.getBoundingClientRect();
    const displayedWidth = imgRect.width;
    const displayedHeight = imgRect.height;
    
    // Calculate the scale factor from displayed to natural
    const pixelRatio = imgNaturalWidth / displayedWidth;
    
    // Calculate crop circle center in container coordinates
    const cropCenterX = containerRect.left + containerRect.width / 2;
    const cropCenterY = containerRect.top + containerRect.height / 2;
    
    // Calculate image center in container coordinates
    const imgCenterX = imgRect.left + displayedWidth / 2;
    const imgCenterY = imgRect.top + displayedHeight / 2;
    
    // Calculate offset from crop center to image center (in natural pixels)
    const offsetX = (imgCenterX - cropCenterX) * pixelRatio;
    const offsetY = (imgCenterY - cropCenterY) * pixelRatio;
    
    // Calculate the crop size in natural pixels
    const cropSizeNatural = (CROP_SIZE / scale) * pixelRatio;
    
    // Calculate source rectangle in natural image coordinates
    const sourceX = (imgNaturalWidth / 2) - (cropSizeNatural / 2) - offsetX;
    const sourceY = (imgNaturalHeight / 2) - (cropSizeNatural / 2) - offsetY;
    
    // Clamp source coordinates to image bounds
    const clampedSourceX = Math.max(0, Math.min(sourceX, imgNaturalWidth - cropSizeNatural));
    const clampedSourceY = Math.max(0, Math.min(sourceY, imgNaturalHeight - cropSizeNatural));
    const clampedSize = Math.min(cropSizeNatural, imgNaturalWidth - clampedSourceX, imgNaturalHeight - clampedSourceY);
    
    // Handle rotation
    ctx.save();
    ctx.translate(CROP_SIZE / 2, CROP_SIZE / 2);
    ctx.rotate((rotation * Math.PI) / 180);
    ctx.translate(-CROP_SIZE / 2, -CROP_SIZE / 2);

    // Draw the cropped portion of the image
    ctx.drawImage(
      img,
      clampedSourceX,
      clampedSourceY,
      clampedSize,
      clampedSize,
      0,
      0,
      CROP_SIZE,
      CROP_SIZE
    );
    ctx.restore();

    // Convert canvas to blob with high quality to preserve crop accuracy
    // Quality 0.95 ensures minimal compression artifacts that could shift pixels
    canvas.toBlob((blob) => {
      if (blob) {
        const croppedFile = new File([blob], imageFile.name, {
          type: 'image/jpeg',
          lastModified: Date.now(),
        });
        onCrop(croppedFile);
      }
    }, 'image/jpeg', 0.95); // High quality to preserve exact crop selection
  };

  if (!isOpen || !imageSrc) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onCancel}>
      <DialogContent className="max-w-md p-0 gap-0">
        <DialogHeader className="px-6 pt-6 pb-4">
          <DialogTitle>Position Your Photo</DialogTitle>
        </DialogHeader>
        
        <div className="px-6 pb-4">
          <div
            ref={containerRef}
            className="relative mx-auto bg-gray-100 rounded-full overflow-hidden"
            style={{
              width: CROP_SIZE,
              height: CROP_SIZE,
              touchAction: 'none',
            }}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            {/* Circular mask overlay */}
            <div
              className="absolute inset-0 rounded-full border-4 border-white shadow-lg z-10 pointer-events-none"
              style={{
                boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.5)',
              }}
            />
            
            {/* Image container - shows full image, can be dragged and zoomed */}
            <div
              className="absolute"
              style={{
                transform: `translate(${position.x}px, ${position.y}px) scale(${scale}) rotate(${rotation}deg)`,
                transformOrigin: 'center center',
                cursor: isDragging ? 'grabbing' : 'grab',
                left: '50%',
                top: '50%',
                // Container sized to show full image at natural aspect ratio
                // Base size is CROP_SIZE, scaled by the image's aspect ratio
                width: imageSize.width > 0 && imageSize.height > 0 
                  ? `${CROP_SIZE * (imageSize.width / Math.max(imageSize.width, imageSize.height))}px`
                  : `${CROP_SIZE}px`,
                height: imageSize.width > 0 && imageSize.height > 0
                  ? `${CROP_SIZE * (imageSize.height / Math.max(imageSize.width, imageSize.height))}px`
                  : `${CROP_SIZE}px`,
                marginLeft: imageSize.width > 0 && imageSize.height > 0
                  ? `-${(CROP_SIZE * (imageSize.width / Math.max(imageSize.width, imageSize.height))) / 2}px`
                  : `-${CROP_SIZE / 2}px`,
                marginTop: imageSize.width > 0 && imageSize.height > 0
                  ? `-${(CROP_SIZE * (imageSize.height / Math.max(imageSize.width, imageSize.height))) / 2}px`
                  : `-${CROP_SIZE / 2}px`,
              }}
            >
              <img
                ref={imageRef}
                src={imageSrc}
                alt="Profile"
                className="select-none"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain', // Show full image, don't crop
                  userSelect: 'none',
                  pointerEvents: 'none',
                  display: 'block',
                }}
                draggable={false}
              />
            </div>
          </div>

          {/* Controls */}
          <div className="flex items-center justify-center gap-4 mt-6">
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={handleZoomOut}
              aria-label="Zoom out"
            >
              <ZoomOut className="h-4 w-4" />
            </Button>
            
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={handleRotate}
              aria-label="Rotate"
            >
              <RotateCw className="h-4 w-4" />
            </Button>
            
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={handleZoomIn}
              aria-label="Zoom in"
            >
              <ZoomIn className="h-4 w-4" />
            </Button>
          </div>

          {/* Instructions */}
          <p className="text-sm text-gray-500 text-center mt-4">
            Drag to position • Pinch or use buttons to zoom
          </p>
        </div>

        {/* Hidden canvas for cropping */}
        <canvas ref={canvasRef} className="hidden" />

        {/* Action buttons */}
        <div className="flex gap-2 px-6 pb-6">
          <Button
            type="button"
            variant="secondary"
            onClick={onCancel}
            className="flex-1"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleCrop}
            className="flex-1"
          >
            Save
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

